import React from 'react'
import { useMutation } from '@apollo/client'
import Button from 'components/Button'
import {
  UserFieldName,
  useUserForm,
  UserFormValues,
  User_user,
  getUserGqlCompliant,
  EditUser,
  EditUserVariables,
  AddressInput as AddressInputType,
} from '@modmd/data'
import Card from 'components/Card'
import { Box } from 'components/Layout'
import { AddressInput } from 'components/AddressInput'
import { useToaster } from 'utils/useToaster'
import { FULL_USER } from 'sharedOperations/user/operations'
import { EDIT_USER } from './operations'

interface UserTestProps {
  refetch?: () => void
  onDismiss: () => void
  data: User_user | null | undefined
}

const EditAddress = ({ refetch, onDismiss, data }: UserTestProps) => {
  const { setToastMessage } = useToaster()

  const [editUser, { loading: isEditLoading }] = useMutation<EditUser, EditUserVariables>(
    EDIT_USER,
    {
      onCompleted: () => {
        setToastMessage('Data successfully changed', 'success')
      },
      refetchQueries: [
        {
          query: FULL_USER,
          variables: {
            id: data?.id?.toString() ?? '',
          },
        },
      ],
      awaitRefetchQueries: true,
    }
  )

  const userForm = useUserForm({
    validationSchema: data?.parentId ? 'subpersonUser' : 'fullUser',
    onSubmit: (values) => {
      void editUser({
        variables: {
          inputData: getUserGqlCompliant(values, [
            UserFieldName.ID,
            UserFieldName.FIRSTNAME,
            UserFieldName.LASTNAME,
            UserFieldName.BIRTHDATE,
            UserFieldName.GENDER,
            UserFieldName.PHONE_NUMBER,
            UserFieldName.ADDRESS,
            UserFieldName.ETHNICITY,
            UserFieldName.RACE,
          ]),
        },
      })
      if (refetch) refetch()
      onDismiss()
    },
  })

  const handleAddressChange = React.useCallback(
    (addressInput: Partial<AddressInputType>) => {
      void userForm.setFieldValue('Address', { ...addressInput })
    },
    [userForm]
  )

  React.useEffect(() => {
    void userForm.setValues({
      ...data,
    } as UserFormValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  return (
    <Card.Content display="grid" gridGap="1rem">
      <Box>
        <AddressInput
          onTouched={(touched) => userForm.setTouched({ ...userForm.touched, Address: touched })}
          errors={userForm.errors.Address}
          touched={userForm.touched.Address}
          value={userForm.values.Address}
          onChange={handleAddressChange}
        />
        <Box mt="2rem" />
        <Button isFetching={isEditLoading} onClick={() => userForm.handleSubmit()}>
          Submit data
        </Button>
      </Box>
    </Card.Content>
  )
}

export { EditAddress }
